// =============================================================================
// Composant FilePreview - Design Apple glassmorphism élégant
// =============================================================================

import React, { useEffect, useState } from 'react';
import { File, FileText, Image, Trash2, Download, FileVideo, FileAudio } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UploadedFile } from './FileUpload';
import ExcelIcon from '../../assets/img3d/ExcelIcon.png';
import WordIcon from '../../assets/img3d/WordIcon.png';
import PDFIcon from '../../assets/img3d/PDFIcon.png';
import IMGIcon from '../../assets/img3d/IMGIcon.png';

// Types pour les différents formats de documents
export type DocumentType = 
  | 'pdf' 
  | 'doc' 
  | 'docx' 
  | 'txt' 
  | 'xlsx'
  | 'xls'
  | 'csv'
  | 'jpg' 
  | 'jpeg' 
  | 'png' 
  | 'gif' 
  | 'mp4' 
  | 'avi' 
  | 'mp3' 
  | 'wav' 
  | 'other';

interface FilePreviewProps {
  files: UploadedFile[];
  onRemoveFile?: (fileId: string) => void;
  onDownloadFile?: (file: UploadedFile) => void;
  className?: string;
}

// Configuration des icônes et couleurs par type de document
const documentConfig = {
  pdf: {
    icon: PDFIcon,
    color: 'text-red-600',
    bgColor: 'bg-gradient-to-br from-red-50 to-red-100',
    circleColor: 'bg-white',
    label: 'PDF'
  },
  doc: {
    icon: WordIcon,
    color: 'text-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
    circleColor: 'bg-white',
    label: 'DOC'
  },
  docx: {
    icon: WordIcon,
    color: 'text-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
    circleColor: 'bg-white',
    label: 'DOCX'
  },
  txt: {
    icon: FileText,
    color: 'text-slate-500',
    bgColor: 'bg-gradient-to-br from-slate-50 to-slate-100',
    circleColor: 'bg-white',
    label: 'TXT'
  },
  xlsx: {
    icon: ExcelIcon,
    color: 'text-emerald-600',
    bgColor: 'bg-gradient-to-br from-emerald-50 to-green-100',
    circleColor: 'bg-white',
    label: 'XLSX'
  },
  xls: {
    icon: ExcelIcon,
    color: 'text-emerald-600',
    bgColor: 'bg-gradient-to-br from-emerald-50 to-green-100',
    circleColor: 'bg-white',
    label: 'XLS'
  },
  csv: {
    icon: ExcelIcon,
    color: 'text-emerald-600',
    bgColor: 'bg-gradient-to-br from-emerald-50 to-green-100',
    circleColor: 'bg-white',
    label: 'CSV'
  },
  jpg: {
    icon: IMGIcon,
    color: 'text-green-600',
    bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
    circleColor: 'bg-white',
    label: 'JPG'
  },
  jpeg: {
    icon: IMGIcon,
    color: 'text-green-600',
    bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
    circleColor: 'bg-white',
    label: 'JPEG'
  },
  png: {
    icon: IMGIcon,
    color: 'text-green-600',
    bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
    circleColor: 'bg-white',
    label: 'PNG'
  },
  gif: {
    icon: Image,
    color: 'text-purple-500',
    bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100',
    circleColor: 'bg-white',
    label: 'GIF'
  },
  mp4: {
    icon: FileVideo,
    color: 'text-orange-500',
    bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
    circleColor: 'bg-white',
    label: 'MP4'
  },
  avi: {
    icon: FileVideo,
    color: 'text-orange-500',
    bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
    circleColor: 'bg-white',
    label: 'AVI'
  },
  mp3: {
    icon: FileAudio,
    color: 'text-indigo-500',
    bgColor: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
    circleColor: 'bg-white',
    label: 'MP3'
  },
  wav: {
    icon: FileAudio,
    color: 'text-indigo-500',
    bgColor: 'bg-gradient-to-br from-indigo-50 to-indigo-100',
    circleColor: 'bg-white',
    label: 'WAV'
  },
  other: {
    icon: File,
    color: 'text-gray-500',
    bgColor: 'bg-gradient-to-br from-gray-50 to-gray-100',
    circleColor: 'bg-white',
    label: 'FILE'
  }
};

// Fonction pour détecter le type de document
const getDocumentType = (fileType: string, fileName: string): DocumentType => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  if (fileType.startsWith('image/')) {
    if (extension === 'jpg') return 'jpg';
    if (extension === 'jpeg') return 'jpeg';
    if (extension === 'png') return 'png';
    if (extension === 'gif') return 'gif';
    return 'jpg'; // fallback
  }
  
  // Détection des fichiers Excel
  if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || extension === 'xlsx') return 'xlsx';
  if (fileType === 'application/vnd.ms-excel' || extension === 'xls') return 'xls';
  if (fileType === 'text/csv' || extension === 'csv') return 'csv';
  
  if (fileType === 'application/pdf' || extension === 'pdf') return 'pdf';
  if (extension === 'doc') return 'doc';
  if (extension === 'docx') return 'docx';
  if (extension === 'txt' || fileType.startsWith('text/')) return 'txt';
  if (extension === 'mp4') return 'mp4';
  if (extension === 'avi') return 'avi';
  if (extension === 'mp3') return 'mp3';
  if (extension === 'wav') return 'wav';
  
  return 'other';
};

// Fonction pour formater la taille de fichier
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Prévisualisation locale pour images, PDFs et textes
const useLocalPreview = (file: File, type: DocumentType, serverUrl?: string) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [textSnippet, setTextSnippet] = useState<string>('');

  useEffect(() => {
    setObjectUrl('');
    setTextSnippet('');
    
    const isImageType = type === 'jpg' || type === 'jpeg' || type === 'png' || type === 'gif';
    const isPdfType = type === 'pdf';
    const isTextType = type === 'txt' || file.type.startsWith('text/');
    
    // If we have a server URL for images, use it directly
    if (serverUrl && isImageType) {
      setObjectUrl(serverUrl);
      return;
    }
    
    // For PDFs and images without server URL, create object URL from file
    if ((isImageType || isPdfType) && file.size > 0) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    
    // For PDFs with server URL, use the server URL
    if (serverUrl && isPdfType) {
      setObjectUrl(serverUrl);
      return;
    }
    
    if (isTextType && file.size > 0) {
      file
        .text()
        .then((txt) => setTextSnippet((txt || '').slice(0, 300)))
        .catch(() => setTextSnippet(''));
    }
  }, [file, type, serverUrl]);

  return { objectUrl, textSnippet };
};

// Composant pour afficher une carte de fichier avec le style glassmorphism
const FilePreviewCard = ({ 
  file, 
  onRemoveFile, 
  onDownloadFile,
  excelBadgeLabel
}: { 
  file: UploadedFile; 
  onRemoveFile?: (fileId: string) => void; 
  onDownloadFile?: (file: UploadedFile) => void; 
  excelBadgeLabel: string;
}) => {
  const documentType = getDocumentType(file.type, file.name);
  const config = documentConfig[documentType];
  const IconComponent = config.icon;
  const { objectUrl, textSnippet } = useLocalPreview(file.file, documentType, file.url);

  const isImageType = documentType === 'jpg' || documentType === 'jpeg' || documentType === 'png' || documentType === 'gif';
  const isPdfType = documentType === 'pdf';
  const isExcelType = documentType === 'xlsx' || documentType === 'xls' || documentType === 'csv';

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownloadFile?.(file);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveFile?.(file.id);
  };

  return (
    <div
      className="
        relative w-56 h-48 rounded-2xl p-3 cursor-pointer flex-shrink-0
        transition-all duration-500 ease-out
        bg-gray-100/80 backdrop-blur-xl
        border-2 border-white/60 shadow-inner
        group overflow-hidden
        hover:shadow-2xl hover:border-white/80
      "
      style={{
        boxShadow: `
          inset 0 1px 0 rgba(255, 255, 255, 0.8),
          inset 0 -1px 0 rgba(255, 255, 255, 0.3),
          0 4px 20px rgba(0, 0, 0, 0.08)
        `
      }}
      onMouseEnter={(e) => {
        // Effet de glow au hover
        e.currentTarget.style.boxShadow = `
          inset 0 1px 0 rgba(255, 255, 255, 0.9),
          inset 0 -1px 0 rgba(255, 255, 255, 0.4),
          0 8px 40px rgba(0, 0, 0, 0.15),
          0 0 20px rgba(59, 130, 246, 0.1)
        `;
      }}
      onMouseLeave={(e) => {
        // Retour à l'ombre normale
        e.currentTarget.style.boxShadow = `
          inset 0 1px 0 rgba(255, 255, 255, 0.8),
          inset 0 -1px 0 rgba(255, 255, 255, 0.3),
          0 4px 20px rgba(0, 0, 0, 0.08)
        `;
      }}
    >
      {/* Double bordure intérieure */}
      <div className="absolute inset-1 rounded-xl border border-white/40"></div>
      
      {/* Layer de prévisualisation pour les images et PDFs */}
      {(isImageType || isPdfType) && objectUrl && (
        <div className="absolute inset-1 rounded-xl overflow-hidden z-0">
          {isImageType && (
            <div
              className="w-full h-full bg-center bg-cover"
              style={{ backgroundImage: `url(${objectUrl})`, filter: 'saturate(1) brightness(1)' }}
            />
          )}
          {isPdfType && (
            <iframe
              className="w-full h-full pointer-events-none"
              src={`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="aperçu-pdf"
            />
          )}
          {/* Voile très subtil pour lisibilité du texte */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/20"></div>
        </div>
      )}

      {/* Layer de prévisualisation pour les fichiers Excel */}
      {isExcelType && (
        <div className="absolute inset-1 rounded-xl overflow-hidden z-0 bg-gradient-to-br from-emerald-50/80 to-green-50/60">
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-4">
              <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                {typeof config.icon === 'string' ? (
                  <img 
                    src={config.icon} 
                    alt={config.label} 
                    className="h-12 w-12" 
                  />
                ) : (
                  <IconComponent className={`h-12 w-12 ${config.color}`} />
                )}
              </div>
              <div className="text-xs font-medium text-emerald-700">
                {excelBadgeLabel}
              </div>
              <div className="text-xs text-emerald-600 mt-1">
                {config.label}
              </div>
            </div>
          </div>
          {/* Voile très subtil */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/20"></div>
        </div>
      )}
      
      {/* Contenu */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Icône en haut à gauche */}
        <div className="flex justify-start mb-2">
          {typeof config.icon === 'string' ? (
            <img 
              src={config.icon} 
              alt={config.label} 
              className="h-8 w-8 group-hover:scale-110 transition-transform duration-300" 
            />
          ) : (
            <IconComponent className={`h-8 w-8 ${config.color} group-hover:scale-110 transition-transform duration-300`} />
          )}
        </div>

        {/* Titre */}
        <div className="mb-2">
          <h3 className="
            text-base font-semibold text-gray-800 leading-tight
            group-hover:text-gray-900 group-hover:font-bold
            transition-all duration-300 ease-out
          ">
            {file.name}
          </h3>
        </div>

        {/* Métadonnées */}
        <div className="mb-3 space-y-1">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="
              px-2 py-1 rounded-md bg-white/70 backdrop-blur-sm font-medium text-gray-700
              group-hover:bg-white/90 group-hover:shadow-sm
              transition-all duration-300 ease-out
            ">
              {config.label}
            </span>
            <span className="
              text-gray-500 group-hover:text-gray-600
              transition-colors duration-300
            ">
              {formatFileSize(file.size)}
            </span>
          </div>
        </div>

        {/* Contenu/Description pour les fichiers texte (pas pour images/PDFs qui ont une preview) */}
        {textSnippet && !isImageType && !isPdfType && (
          <div className="rounded-md bg-white/70 ring-1 ring-white/60 p-2 h-24 overflow-auto">
            <p className="text-xs text-gray-600 leading-relaxed">{textSnippet}</p>
          </div>
        )}

        {/* Actions au hover */}
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out group-hover:translate-y-0 translate-y-1">
          {onDownloadFile && (
            <button
              onClick={handleDownload}
              className="
                w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm 
                flex items-center justify-center 
                hover:bg-white hover:scale-110 hover:shadow-lg
                transition-all duration-300 ease-out
                shadow-sm border border-white/60
                hover:border-blue-300/60
              "
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <Download className="h-3.5 w-3.5 text-gray-600 hover:text-blue-600 transition-colors duration-300" />
            </button>
          )}
          {onRemoveFile && (
            <button
              onClick={handleRemove}
              className="
                w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm 
                flex items-center justify-center 
                hover:bg-white hover:scale-110 hover:shadow-lg
                transition-all duration-300 ease-out
                shadow-sm border border-white/60
                hover:border-red-300/60
              "
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <Trash2 className="h-3.5 w-3.5 text-red-600 hover:text-red-700 transition-colors duration-300" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const FilePreview = ({ files, onRemoveFile, onDownloadFile, className }: FilePreviewProps) => {
  // Ne rien afficher si aucun fichier n'a été uploadé
  if (files.length === 0) {
    return null;
  }

  return (
    <div className={cn('w-full space-y-2', className)}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/20 flex items-center justify-center">
          <File className="h-4 w-4 text-blue-600/80" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900/90">
          Fichiers ({files.length})
        </h3>
      </div>
      
      <div className="flex flex-wrap justify-start items-start gap-4 p-2">
        {files.map((file, index) => (
          <div
            key={file.id}
            className="relative group transition-all duration-300 ease-out hover:z-50"
            onMouseEnter={(e) => {
              // Animation de hover : la carte passe au premier plan
              e.currentTarget.style.zIndex = '999';
              e.currentTarget.style.transform = 'translateY(-8px) scale(1.05)';
            }}
            onMouseLeave={(e) => {
              // Retour à la position normale
              e.currentTarget.style.zIndex = '1';
              e.currentTarget.style.transform = 'translateY(0px) scale(1)';
            }}
          >
            <FilePreviewCard
              file={file}
              onRemoveFile={onRemoveFile}
              onDownloadFile={onDownloadFile}
              excelBadgeLabel="Fichier Excel"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilePreview;